// src/components/ecommerce/ProductListItem.jsx
import React, { useState } from 'react';
import { 
  ShoppingCart,
  Heart,
  Star,
  Eye,
  Share2,
  Package,
  Tag,
  Truck,
  Shield,
  Info,
  Plus,
  Minus,
  Check
} from 'lucide-react';

export const ProductListItem = ({ 
  product, 
  onProductClick, 
  onAddToCart, 
  onAddToWishlist,
  showQuickAdd = true,
  showWishlist = true 
}) => {
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart?.(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    setIsInWishlist(!isInWishlist);
    onAddToWishlist?.(product);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const currentPrice = product.salePrice || product.price || 0;
  const originalPrice = product.price || 0;
  const hasDiscount = product.salePrice && product.salePrice < originalPrice;
  const discountPercent = hasDiscount ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;

  const stockStatus = product.quantity > 0 ? 'in-stock' : 'out-of-stock';
  const lowStock = product.quantity <= 5 && product.quantity > 0;

  return (
    <div 
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onProductClick?.(product)}
    >
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="relative flex-shrink-0">
          <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
            {product.images?.[0] || product.imageUrl ? (
              <img
                src={product.images?.[0] || product.imageUrl}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>

          {/* Discount Badge */}
          {hasDiscount && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              -{discountPercent}%
            </div>
          )}

          {/* Stock Status Badge */}
          {lowStock && (
            <div className="absolute -bottom-2 -right-2 bg-orange-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              {product.quantity} left
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                {product.name}
              </h3>
              
              {product.brand && (
                <p className="text-sm text-gray-500 mb-1">{product.brand}</p>
              )}

              {product.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                  {product.description}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-4">
              {showWishlist && (
                <button
                  onClick={handleWishlistToggle}
                  className={`p-2 rounded-lg transition-colors ${
                    isInWishlist 
                      ? 'bg-red-100 text-red-600' 
                      : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600'
                  }`}
                  title="Add to wishlist"
                >
                  <Heart className={`h-4 w-4 ${isInWishlist ? 'fill-current' : ''}`} />
                </button>
              )}
              
              <button
                onClick={handleShare}
                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-blue-100 hover:text-blue-600 transition-colors"
                title="Share product"
              >
                <Share2 className="h-4 w-4" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onProductClick?.(product);
                }}
                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-blue-100 hover:text-blue-600 transition-colors"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Rating */}
          {product.rating && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(product.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {product.rating.toFixed(1)} ({product.reviewCount || 0} reviews)
              </span>
            </div>
          )}

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {product.tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
              {product.tags.length > 3 && (
                <span className="text-xs text-gray-500">+{product.tags.length - 3} more</span>
              )}
            </div>
          )}

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                {product.features.slice(0, 2).map((feature, index) => (
                  <div key={index} className="flex items-center gap-1 text-xs text-gray-600">
                    <Check className="h-3 w-3 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
                {product.features.length > 2 && (
                  <span className="text-xs text-gray-500">+{product.features.length - 2} more features</span>
                )}
              </div>
            </div>
          )}

          {/* Price and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Price */}
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-gray-900">
                  R{currentPrice.toFixed(2)}
                </span>
                {hasDiscount && (
                  <span className="text-sm text-gray-500 line-through">
                    R{originalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-1">
                {stockStatus === 'in-stock' ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600 font-medium">In Stock</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-red-600 font-medium">Out of Stock</span>
                  </>
                )}
              </div>
            </div>

            {/* Add to Cart Section */}
            {showQuickAdd && stockStatus === 'in-stock' && (
              <div className="flex items-center gap-2">
                {/* Quantity Selector */}
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuantity(Math.max(1, quantity - 1));
                    }}
                    className="p-1 hover:bg-gray-100 rounded-l-lg"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={product.quantity}
                    value={quantity}
                    onChange={(e) => {
                      e.stopPropagation();
                      setQuantity(Math.max(1, Math.min(product.quantity, parseInt(e.target.value) || 1)));
                    }}
                    className="w-12 text-center text-sm border-0 focus:ring-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuantity(Math.min(product.quantity, quantity + 1));
                    }}
                    className="p-1 hover:bg-gray-100 rounded-r-lg"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={addedToCart}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    addedToCart
                      ? 'bg-green-500 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <Check className="h-4 w-4" />
                      Added!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              {product.freeShipping && (
                <div className="flex items-center gap-1">
                  <Truck className="h-3 w-3" />
                  <span>Free Shipping</span>
                </div>
              )}
              
              {product.warranty && (
                <div className="flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  <span>{product.warranty} Warranty</span>
                </div>
              )}
              
              {product.sku && (
                <span>SKU: {product.sku}</span>
              )}
            </div>

            {product.category && (
              <span className="bg-gray-100 px-2 py-1 rounded">
                {product.category}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};